import { useAuth0 } from '#~/auth/index.js';
import { toUrx } from '#~/device/index.js';
import { logger } from '#~/logger/index.js';
import { raiseError } from '#~/notifications/index.js';
import { useProducer, useStore } from './hooks.js';
import { addDevice, removePreview } from './utils.js';
import type { ChildrenOnly } from '@studio/design-system/props';
import { getTransientId } from '@studio/models/device';
import { useDeepEffect, useLatestFn } from '@studio/react-utils/hooks';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { shallow } from 'zustand/shallow';

/**
 * `<Mediator>` component.
 *
 * This provider-like component is responsible for synchronizing the audio device candidate with the
 * temporary preview device on the encoder agent.
 */
export const Mediator = ({ children }: ChildrenOnly): ReactElement => {
  const producer = useProducer();
  const { getAccessTokenSilently } = useAuth0();

  const { agentId, appId, candidate, previews, roomId } = useStore(
    (µ) => pick(µ, ['agentId', 'appId', 'candidate', 'previews', 'roomId']),
    shallow
  );

  const synchronize = useLatestFn(async () => {
    const token = await getAccessTokenSilently();
    const preview = previews.audio;

    // Generate the URX for the candidate device.
    const urx = toUrx(
      {
        audio: candidate.audio
      },
      { isTransient: true }
    );

    // Is there a candidate device?
    if (urx) {
      // Is the preview device already in sync?
      if (preview?.urx === urx) {
        return;
      }

      // Remove the out-of-sync preview device.
      if (preview) {
        // Reset the preview record.
        producer((draft) => {
          draft.previews.audio = undefined;
        });

        // Wait for the preview device to be removed.
        try {
          await removePreview(preview, token);
        } catch (error) {
          logger.info(error as Error);
        }
      }

      // Generate a new preview device.
      const deviceId = getTransientId(appId);

      logger.debug(`Adding preview audio device: ${deviceId}`);

      // Create the preview device.
      addDevice(roomId, agentId, { ...candidate, agentId, id: deviceId }, token)
        .then(() => {
          // Update the preview record.
          producer((draft) => {
            draft.previews.audio = { deviceId, urx };
          });
        })
        .catch((error: Error) => {
          // Errors will be fairly common here so treat them as `info` messages.
          logger.info(error);

          // Keep track of the error.
          producer((draft) => {
            draft.previews.audio = { deviceId, error, urx };
          });
        });

      return;
    }

    // Remove preview device (if applicable).
    if (preview) {
      // Reset the preview record.
      producer((draft) => {
        draft.previews.audio = undefined;
      });

      removePreview(preview, token).catch((error: Error) => {
        logger.info(error);
      });
    }
  });

  // Watch the candidate devices for changes.
  useDeepEffect(() => {
    // Background add-flow sync: it re-runs when the candidate set changes and
    // asks nothing of the user, so a failure must not light the bell.
    synchronize().catch(raiseError);

    /*return () => {
      synchronize(true);
    };*/
  }, [candidate]);

  return <>{children}</>;
};
